/**
 * Уборка СВОИХ протухших RTC-членств.
 *
 * ЗАЧЕМ (замер на боевом Synapse, 2026-09-26):
 *   В комнате !MYvaayASwXPpvZRQhW с 18:14:51 висело членство
 *   `_@ab:algsuccess.com_leGqcZcGAQ_m.call` с непустым content. Вкладку,
 *   которая его опубликовала, закрыли — `teardown()` не отработал, и
 *   членство осталось лежать в состоянии комнаты с `expires: 14400000`
 *   (4 часа). Последствия замерены, их два, и оба ломают звонки ПОЛНОСТЬЮ:
 *
 *   1. RING НЕ ОТПРАВЛЯЕТСЯ НИ ИЗ КАКОГО КЛИЕНТА.
 *      `matrix-js-sdk` 42.3.0, `MatrixRTCSession.js:263`:
 *          if (this.pendingNotificationToSend && ownMembership
 *              && oldMemberships.length === 0) { ... sendCallNotify(...) }
 *      Уведомление шлёт только тот, кто вошёл в ПУСТУЮ сессию. Пока в
 *      комнате лежит фантом, `oldMemberships.length !== 0` — и ring не
 *      рождается. Замер того же дня: @dinussy входила в сессию 4 раза
 *      (18:28, 18:29, 19:22, 19:23) и НИ РАЗУ не отправила notify.
 *      Нет ring -> нет push action у Synapse -> нет VoIP-пуша -> телефон
 *      молчит совершенно законно.
 *
 *   2. КНОПКА ЗВОНКА ПРЕВРАЩАЕТСЯ В «ПРИСОЕДИНИТЬСЯ».
 *      `useCallPresence` считает фантом активным участником, и комната
 *      выглядит как «идёт звонок», которого нет.
 *
 * ПОЧЕМУ НЕ ХВАТАЕТ `isExpired()`:
 *   Для Session-членств SDK смотрит только `expires` (`CallMembership.js:388`),
 *   то есть календарь: 4 часа фантом считается живым. Нам нужен критерий
 *   надёжнее календаря — и он есть: МОЁ СОБСТВЕННОЕ устройство знает, есть
 *   ли у него живая локальная сессия. Если членство подписано моим userId,
 *   но НЕ моим deviceId — это заведомо мёртвый след прошлой вкладки, и ждать
 *   четыре часа незачем. Членство моего же deviceId не трогаем никогда:
 *   его владелец — текущая вкладка, и снимать его — забота `teardown()`.
 *
 * ГРАНИЦЫ (важно):
 *   • Трогаются ТОЛЬКО членства собственного userId. Чужие — никогда,
 *     ни живые, ни протухшие: чужую уборку делает чужой клиент.
 *   • Снятие — пустой content поверх, РОВНО как это делает сам SDK при
 *     выходе. Не redaction: состояние комнаты остаётся консистентным.
 *   • Ошибка уборки НЕ мешает звонку: звоним дальше, просто без ring.
 */

import type { MatrixClient, Room } from "matrix-js-sdk";
import { debugLogL } from "../debug";

/** Тип состояния MatrixRTC-членства, который публикует наш клиент. */
const CALL_MEMBER_TYPE = "org.matrix.msc3401.call.member";

/**
 * Снять свои мёртвые RTC-членства в комнате.
 *
 * @returns сколько членств снято (0 — если убирать было нечего).
 */
export async function clearStaleOwnCallMemberships(
  client: MatrixClient,
  room: Room,
): Promise<number> {
  const myUserId = client.getUserId();
  const myDeviceId = client.getDeviceId();
  if (!myUserId) return 0;

  const state = room.getLiveTimeline().getState("f" as never);
  if (!state) return 0;

  const events = state.getStateEvents(CALL_MEMBER_TYPE) ?? [];
  let cleared = 0;

  for (const ev of events) {
    const stateKey = ev.getStateKey();
    if (!stateKey) continue;

    // Уже пустое — снимать нечего. Именно так выглядит штатный выход.
    const content = ev.getContent() as Record<string, unknown>;
    if (!content || Object.keys(content).length === 0) continue;

    // ТОЛЬКО СВОИ. Чужое членство не наша забота ни при каких условиях.
    if (ev.getSender() !== myUserId) continue;

    // СВОЁ ТЕКУЩЕЕ УСТРОЙСТВО НЕ ТРОГАЕМ: если эта вкладка реально в
    // звонке, её членство живое, и снять его должен `teardown()`.
    // `device_id` лежит в content; `membershipID` — запасной источник
    // (формат `@user:server:DEVICEID`), потому что у части наших событий
    // device_id не заполнен.
    const deviceFromContent =
      typeof content.device_id === "string" ? content.device_id : null;
    const membershipId =
      typeof content.membershipID === "string" ? content.membershipID : null;
    const deviceFromMembershipId = membershipId
      ? membershipId.slice(membershipId.lastIndexOf(":") + 1)
      : null;
    const owningDevice = deviceFromContent ?? deviceFromMembershipId;

    if (myDeviceId && owningDevice && owningDevice === myDeviceId) continue;

    try {
      await client.sendStateEvent(
        room.roomId,
        CALL_MEMBER_TYPE as never,
        {} as never,
        stateKey as never,
      );
      cleared += 1;
      debugLogL(
        `[call] cleared stale own membership room=${room.roomId} stateKey=${stateKey} device=${owningDevice ?? "unknown"}`,
      );
    } catch (e) {
      // Прав может не хватить (state_default=50). Это не повод отменять
      // звонок: хуже, чем есть, не станет.
      debugLogL(
        `[call] could NOT clear stale membership room=${room.roomId} stateKey=${stateKey}: ${String(e)}`,
      );
    }
  }

  return cleared;
}

/**
 * Живо ли членство ДЛЯ ЦЕЛЕЙ ОТОБРАЖЕНИЯ «идёт звонок».
 *
 * Членство собственного userId с ЧУЖОГО устройства не считается признаком
 * идущего звонка: ровно оно и рисовало Антону «звонок висит, как будто
 * продолжается» после отбоя в Element X.
 */
export function isForeignDeviceOwnMembership(
  membershipUserId: string | undefined,
  membershipDeviceId: string | undefined,
  myUserId: string | null,
  myDeviceId: string | null,
): boolean {
  if (!membershipUserId || !myUserId) return false;
  if (membershipUserId !== myUserId) return false;
  if (!membershipDeviceId || !myDeviceId) return false;
  return membershipDeviceId !== myDeviceId;
}
